'use client';

import {
	BN,
	BigNum,
	FOUR,
	MarketType,
	PublicKey,
	SpotBalanceType,
	SpotMarketConfig,
	SwapMode,
	UnifiedQuoteResponse,
	convertToNumber,
} from '@drift-labs/sdk';
import { MarketId, matchEnum } from '@drift/common';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'react-use';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { notify } from 'src/utils/notifications';
import useAccountSpotBalances from '../useAccountBankBalances';
import useCurrentSettings from '../useCurrentSettings';
import { useSwap } from '../driftActions/swaps/useSwap';
import useDriftClientIsReady from '../useDriftClientIsReady';
import useAppEventEmitter from '../useAppEventEmitter';
import { getAccountsInUse } from 'src/utils/position';
import { getSimulatedQuoteValues, getSwapToastId } from 'src/utils/swap';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { useExceedsMaxSpotBalance } from '../useMaxSpotBalance';
import useGetOraclePriceForMarket from '../useGetOraclePriceForMarket';
import { useDebouncedCallback } from 'use-debounce';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import useModal from 'src/components/Modals/hooks/useModal';
import { useMarginInfoByUserKey } from '../query/accountTradingData/useMarginInfo';
import { useInvalidateTitanBadgeQueries } from '../useTitanBadge';
import useIsAccountMarginEnabled from 'src/components/TradeForm/hooks/useIsAccountMarginEnabled';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';

const DEFAULT_MAX_ACCOUNTS = 52;
const DEFAULT_MAX_ACCOUNTS_BIG = 60;
const MAX_ACCOUNTS_SWAP_BUFFER = Env.maxAccountsSwapBuffer;

const biggerMaxAccountsMarketIndexes = [
	17, // dSOL
	22, // PYUSD
];

export default function useSwapFormEngine(
	swapFromMarket: SpotMarketConfig,
	swapToMarket: SpotMarketConfig,
	defaultSlippageTolerancePct: number,
	defaultSwapMode: SwapMode = 'ExactIn',
	targetAccountKey?: string
) {
	const { swap } = useSwap();
	const [slippageTolerance, swapClient, authority] = useDriftStore((s) => [
		s.tradeForm.slippageTolerance,
		s.swapClient,
		s.wallet.current.adapter?.publicKey,
	]);
	const [savedSettings, setSavedSettings] = useCurrentSettings();
	const { show } = useModal();
	const { invalidateAll: invalidateTitanBadgeQueries } =
		useInvalidateTitanBadgeQueries();

	const [isMarginCheckboxChecked, setIsMarginCheckboxChecked] =
		useSyncLocalStorage('swapFormMarginCheckboxChecked', false);
	const isAccountMarginEnabled = useIsAccountMarginEnabled();
	const isFormMarginEnabled = isAccountMarginEnabled && isMarginCheckboxChecked;

	const toggleIsMarginCheckboxChecked = useCallback(() => {
		setIsMarginCheckboxChecked((curr) => !curr);
	}, [setIsMarginCheckboxChecked]);

	const spotMarketPoolId = swapFromMarket.poolId;

	if (spotMarketPoolId !== swapToMarket.poolId) {
		throw new Error('Swap from and to markets must be in the same pool');
	}

	const currentUserKey = useDriftAccountStore((s) => s.currentUserKey);
	const effectiveUserKey = targetAccountKey ?? currentUserKey ?? '';
	const targetAccount = useDriftAccountStore(
		(s) => s.accounts[effectiveUserKey]
	);
	const targetUserKey = targetAccount?.userKey;
	const { data: targetMarginInfo } = useMarginInfoByUserKey(
		effectiveUserKey || undefined
	);

	const accountBalances = useAccountSpotBalances(
		targetUserKey,
		undefined,
		spotMarketPoolId
	);
	const driftClientIsReady = useDriftClientIsReady();
	const isGeoblocked = useDriftStore((s) => s.isGeoblocked);
	const setStore = useDriftStore((s) => s.set);
	const appEventEmitter = useAppEventEmitter();
	const willExceedMaxSpotBalance = useExceedsMaxSpotBalance(
		swapToMarket.marketIndex
	);

	const [swapFromAmount, setSwapFromAmount] = useState<string>('');
	const [swapToAmount, setSwapToAmount] = useState<string>('');
	const [quoteDetails, setQuoteDetails] = useState<UnifiedQuoteResponse | null>(
		null
	);
	const [swapPrice, setSwapPrice] = useState(0);
	const [swapMode, setSwapMode] = useState(defaultSwapMode);
	const [isLoading, setIsLoading] = useState(false);
	const [isApprovingTxn, setIsApprovingTxn] = useState(false); // this is needed because it takes a couple of seconds for the wallet approval to show up, and we don't want the user to spam the swap button
	const [
		showPriceEstimateOracleDivergenceWarning,
		setShowPriceEstimateOracleDivergenceWarning,
	] = useState(false);
	const [priceDivergencePct, setPriceDivergencePct] = useState<string>();
	const [maxSwapAmountDetails, setMaxSwapAmountDetails] = useState<{
		inAmount: BN;
		outAmount: BN;
		leverage: BN;
	}>();
	const [additionalAccountsBuffer, setAdditionalAccountsBuffer] = useState(
		MAX_ACCOUNTS_SWAP_BUFFER
	);

	// Use oracle price instead of jup price data because sometimes jup price data feed is unavailable for the pair
	const getOraclePriceForMarket = useGetOraclePriceForMarket();
	const fromTokenOraclePrice = getOraclePriceForMarket(
		new MarketId(swapFromMarket.marketIndex, MarketType.SPOT)
	);
	const toTokenOraclePrice = getOraclePriceForMarket(
		new MarketId(swapToMarket.marketIndex, MarketType.SPOT)
	);
	const tokenPairPrice =
		(fromTokenOraclePrice?.toNum() ?? 0) / (toTokenOraclePrice?.toNum() ?? 0);

	const swapFromMarketIndexRef = useRef(swapFromMarket.marketIndex);
	const swapToMarketIndexRef = useRef(swapToMarket.marketIndex);
	const isApprovingTxnRef = useRef(isApprovingTxn);

	const isExactIn = swapMode === 'ExactIn';
	const enableVersionedTx = savedSettings.enableVersionedTx;
	const directRouteSwaps = savedSettings.directRouteSwaps;

	// input variables
	const fromTokenBalanceAccount = accountBalances.find(
		(acc) => acc.asset.marketIndex === swapFromMarket.marketIndex
	);
	let fromTokenBalance =
		fromTokenBalanceAccount?.balance ??
		BigNum.zero(swapFromMarket.precisionExp);
	fromTokenBalance = matchEnum(
		SpotBalanceType.BORROW,
		fromTokenBalanceAccount?.balanceType
	)
		? fromTokenBalance.neg()
		: fromTokenBalance;

	const swapFromAmountBigNum = BigNum.fromPrint(
		swapFromAmount,
		swapFromMarket.precisionExp
	);

	const swapToAmountBigNum = BigNum.fromPrint(
		swapToAmount,
		swapToMarket.precisionExp
	);

	const desiredSwapAmount = isExactIn
		? swapFromAmountBigNum
		: swapToAmountBigNum;

	const desiredSwapAmountNum = desiredSwapAmount.toNum();

	const inputNumericAmount = isExactIn
		? swapFromAmountBigNum.toNum()
		: swapToAmountBigNum.toNum();

	// margin variables
	const maxLeverageAllowed = BigNum.from(
		maxSwapAmountDetails?.leverage.toString(),
		FOUR
	).toNum();
	const maxSwapSize = calcMaxSwapSize();
	const currentLeverage = targetMarginInfo?.leverage;

	const isInsufficientBalance =
		swapFromAmountBigNum.toNum() > maxSwapSize.toNum();

	const borrowAmount = swapFromAmountBigNum.sub(fromTokenBalance);

	const toTokenBalanceAccount = accountBalances.find(
		(acc) => acc.asset.marketIndex === swapToMarket.marketIndex
	);
	const willReduceBorrow = matchEnum(
		toTokenBalanceAccount?.balanceType,
		SpotBalanceType.BORROW
	);

	const borrowToBeRepaid =
		willReduceBorrow && toTokenBalanceAccount
			? BigNum.min(toTokenBalanceAccount.balance, swapToAmountBigNum)
			: BigNum.zero(toTokenBalanceAccount?.asset.precisionExp);

	// Logic to make sure user input choice matches quote
	// This should prevent users from accidentally swapping with a stale quote that doesn't match the current input
	let marketsMatchQuote = false;
	if (
		quoteDetails &&
		!quoteDetails.error &&
		swapFromMarket &&
		swapToMarket &&
		swapFromAmount &&
		swapToAmount
	) {
		const quoteDetailsInAmountNum = convertToNumber(
			new BN(quoteDetails.inAmount),
			swapFromMarket.precision
		);
		const quoteDetailsOutAmountNum = convertToNumber(
			new BN(quoteDetails.outAmount),
			swapToMarket.precision
		);

		const correctMints =
			quoteDetails.inputMint === swapFromMarket.mint.toString() &&
			quoteDetails.outputMint === swapToMarket.mint.toString();

		const correctMode = isExactIn
			? quoteDetails.swapMode === 'ExactIn'
			: quoteDetails.swapMode === 'ExactOut';

		const correctAmounts = isExactIn
			? parseFloat(swapFromAmount) === quoteDetailsInAmountNum
			: parseFloat(swapToAmount) === quoteDetailsOutAmountNum;

		marketsMatchQuote = correctMints && correctMode && correctAmounts;
	}

	const isAboveMaxSwapSizeWithoutLeverage =
		!isFormMarginEnabled && isInsufficientBalance;

	const isButtonDisabled =
		isLoading ||
		!marketsMatchQuote ||
		!quoteDetails ||
		!enableVersionedTx ||
		isApprovingTxn ||
		isInsufficientBalance ||
		isGeoblocked ||
		!!quoteDetails?.error ||
		willExceedMaxSpotBalance ||
		isAboveMaxSwapSizeWithoutLeverage;

	const [callFetchRoute, setCallFetchRoute] = useState(true);

	// short term fix to default swap form's slippage, otherwise will be using tradeform's slippage
	useEffect(() => {
		setStore((s) => {
			s.tradeForm.slippageTolerance = defaultSlippageTolerancePct;
		});
	}, [defaultSlippageTolerancePct]);

	// Fetching routes from Jupiter is expensive, so we only want to do it when necessary.
	// When we don't need real quotes (e.g. during slider movement), we use simulated amounts
	// to provide instant feedback to the user.
	useEffect(() => {
		if (callFetchRoute) {
			debouncedCallFetchRoute();
		} else {
			updateSimulatedSwapAmount();
		}
	}, [desiredSwapAmountNum, callFetchRoute]);

	useDebounce(
		() => {
			getRoute();
		},
		300,
		[
			swapFromMarket,
			swapToMarket,
			slippageTolerance,
			directRouteSwaps,
			isExactIn,
			additionalAccountsBuffer,
		]
	);

	// listen for successful swap event
	useEffect(() => {
		const handler = (swapRecordEvent: any) => {
			// check if the successful swap event is for the currently transacting swap
			if (
				swapFromMarketIndexRef.current === swapRecordEvent.inMarketIndex &&
				swapToMarketIndexRef.current === swapRecordEvent.outMarketIndex &&
				isApprovingTxnRef.current
			) {
				setIsApprovingTxn(false);
				getRouteRef.current();
			}
		};

		appEventEmitter.on('successfulSwap', handler);

		return () => {
			appEventEmitter.off('successfulSwap', handler);
		};
	}, [appEventEmitter]);

	useEffect(() => {
		if (driftClientIsReady && targetAccount?.client?.isSubscribed) {
			setMaxSwapAmountDetails(
				targetAccount?.client?.getMaxSwapAmount({
					inMarketIndex: swapFromMarket.marketIndex,
					outMarketIndex: swapToMarket.marketIndex,
				})
			);
		}
	}, [
		driftClientIsReady,
		swapFromMarket.marketIndex,
		swapToMarket.marketIndex,
		!!targetAccount?.client,
		fromTokenBalance.toNum(),
	]);

	// refresh optimal route every 30s
	useEffect(() => {
		let interval: ReturnType<typeof setInterval>;
		if (quoteDetails) {
			interval = setInterval(() => getRoute(), 10000);
		}

		return () => clearInterval(interval);
	}, [quoteDetails]);

	// remove other swap amount when route is loading
	useEffect(() => {
		if (isLoading) {
			getSwapConfig(isExactIn).setOtherAmount('');
		}
	}, [isLoading, isExactIn]);

	// update other swap amount when route details change
	useDebounce(
		() => {
			const { otherMarket, setOtherAmount } = getSwapConfig(isExactIn);

			if (!quoteDetails) {
				setOtherAmount('');
				return;
			}

			const amountStr = BigNum.from(
				isExactIn ? quoteDetails.outAmount : quoteDetails.inAmount,
				otherMarket.precisionExp
			).printShort();

			setOtherAmount(amountStr);
		},
		300,
		[quoteDetails, swapFromMarket, swapToMarket, isExactIn]
	);

	// set default swap slippage to 0.1%
	useEffect(() => {
		setStore((s) => {
			s.tradeForm.slippageTolerance = 0.1;
		});
	}, []);

	function calcMaxSwapSize() {
		let maxLeveragedSwapSize = BigNum.from(
			maxSwapAmountDetails?.inAmount,
			swapFromMarket.precisionExp
		);

		// prevent race condition where either variable is not using the correct market yet
		if (!fromTokenBalance.precision.eq(maxLeveragedSwapSize.precision)) {
			return BigNum.zero();
		}

		if (isFormMarginEnabled) {
			// allow a 1% buffer if max size is leveraged, since numbers are constantly updated
			maxLeveragedSwapSize = maxLeveragedSwapSize
				.mul(new BN(99))
				.div(new BN(100));
		}

		let maxSwapSize: BigNum;

		if (isFormMarginEnabled) {
			maxSwapSize = maxLeveragedSwapSize;
		} else {
			if (fromTokenBalance.gt(maxLeveragedSwapSize)) {
				// max swap size should never be greater than the max leveraged size
				maxSwapSize = maxLeveragedSwapSize;
			} else {
				maxSwapSize = fromTokenBalance.isNeg()
					? BigNum.zero(swapFromMarket.precisionExp)
					: fromTokenBalance;
			}
		}

		return maxSwapSize;
	}
	const getRoute = useCallback(async () => {
		setIsLoading(true);
		try {
			if (desiredSwapAmountNum > 0 && targetAccount?.client) {
				const accountsToUse = getAccountsInUse(targetAccount?.client);

				// Use more max accounts for pyusd and dsol, the problem cases
				const useBiggerMaxAccounts = biggerMaxAccountsMarketIndexes.some(
					(index) =>
						swapFromMarket.marketIndex === index ||
						swapToMarket.marketIndex === index
				);

				const maxAccounts = useBiggerMaxAccounts
					? DEFAULT_MAX_ACCOUNTS_BIG -
						Math.min(5, additionalAccountsBuffer) -
						accountsToUse
					: DEFAULT_MAX_ACCOUNTS - additionalAccountsBuffer - accountsToUse;
				const usdEstimate =
					(fromTokenOraclePrice?.toNum() ?? 0) * swapFromAmountBigNum.toNum();

				const slippageParams =
					slippageTolerance === 'dynamic'
						? {
								autoSlippage: true,
								maxAutoSlippageBps: 500, // 5 pct
								usdEstimate: Math.round(usdEstimate),
							}
						: {
								slippageBps: slippageTolerance ? slippageTolerance * 100 : 500, // 5% slippage if user selects inifinite
								autoSlippage: false,
							};

				if (
					slippageParams.slippageBps !== undefined &&
					slippageParams.slippageBps < 1
				) {
					setQuoteDetails({
						inAmount: '0',
						outAmount: '0',
						inputMint: swapFromMarket.mint.toBase58(),
						outputMint: swapToMarket.mint.toBase58(),
						slippageBps: 1,
						otherAmountThreshold: '0',
						swapMode: 'ExactIn',
						errorCode: 'SLIPPAGE_TOO_LOW',
						error: 'Slippage is too low',
						priceImpactPct: '0',
						routePlan: [],
					});
					return;
				}

				const quote = await swapClient?.getQuote({
					inputMint: swapFromMarket.mint,
					outputMint: swapToMarket.mint,
					amount: desiredSwapAmount.val,
					userPublicKey: authority as PublicKey,
					swapMode,
					...(swapClient?.isJupiter() ? { maxAccounts } : {}),
					onlyDirectRoutes: directRouteSwaps,
					...slippageParams,
				});

				if (!quote) {
					console.error('Missing quote response');
					return;
				}

				// Calculate price impact if not provided in quote (e.g., for Titan quotes)
				let quoteWithPriceImpact = quote;
				if (
					!('priceImpactPct' in quote) &&
					fromTokenOraclePrice &&
					toTokenOraclePrice
				) {
					const inputUsdValue =
						convertToNumber(new BN(quote.inAmount), swapFromMarket.precision) *
						fromTokenOraclePrice.toNum();

					const outputUsdValue =
						convertToNumber(new BN(quote.outAmount), swapToMarket.precision) *
						toTokenOraclePrice.toNum();

					const usdDifference = inputUsdValue - outputUsdValue;
					const priceImpactPct =
						inputUsdValue > 0 ? usdDifference / inputUsdValue : 0;

					// Add the calculated price impact to the quote (as decimal, not percentage)
					quoteWithPriceImpact = {
						...quote,
						priceImpactPct: priceImpactPct.toString(),
					};
				}

				setQuoteDetails(quoteWithPriceImpact);

				if (quote.error) {
					console.error('Error in quote response', quote);
					if (
						quote.errorCode === 'ROUTE_PLAN_DOES_NOT_CONSUME_ALL_THE_AMOUNT'
					) {
						// Try a low number for the max accounts buffer if we hit this error
						if (additionalAccountsBuffer > 5 && !useBiggerMaxAccounts) {
							setAdditionalAccountsBuffer(5);
						}
					}
				}

				const swapPrice =
					(+quote.outAmount / +quote.inAmount) *
					10 **
						(swapFromMarket.precisionExp.toNumber() -
							swapToMarket.precisionExp.toNumber());

				const priceDivergence = Math.abs(swapPrice - tokenPairPrice);

				const priceDivergencePct = (priceDivergence / tokenPairPrice) * 100;

				setShowPriceEstimateOracleDivergenceWarning(
					priceDivergencePct > Env.priceDivergenceWarningThreshold
				);
				setPriceDivergencePct(priceDivergencePct.toFixed(2));
				setSwapPrice(swapPrice);
			} else {
				setQuoteDetails(null);
				setSwapPrice(0);
			}
		} catch (err) {
			console.error('Error fetching jupiter routes', err);
			setQuoteDetails(null);
			setSwapPrice(0);
		} finally {
			setIsLoading(false);
		}
	}, [
		desiredSwapAmountNum,
		targetAccount?.client,
		additionalAccountsBuffer,
		fromTokenOraclePrice,
		swapFromAmountBigNum,
		slippageTolerance,
		swapClient,
		swapFromMarket.mint,
		swapFromMarket.precisionExp,
		swapFromMarket.marketIndex,
		swapToMarket.mint,
		swapToMarket.precisionExp,
		swapToMarket.marketIndex,
		desiredSwapAmount.val,
		swapMode,
		directRouteSwaps,
		tokenPairPrice,
	]);

	const getRouteRef = useRef(getRoute);

	// sync swap market indexes with their refs
	useEffect(() => {
		swapFromMarketIndexRef.current = swapFromMarket.marketIndex;
		swapToMarketIndexRef.current = swapToMarket.marketIndex;
		isApprovingTxnRef.current = isApprovingTxn;
		getRouteRef.current = getRoute;
	}, [
		swapFromMarket.marketIndex,
		swapToMarket.marketIndex,
		isApprovingTxn,
		getRoute,
	]);

	const debouncedCallFetchRoute = useDebouncedCallback(getRoute, 300);

	function updateSimulatedSwapAmount() {
		if (!isFinite(tokenPairPrice) || tokenPairPrice <= 0) {
			if (swapMode === 'ExactIn') {
				setSwapToAmount('0');
			} else {
				setSwapFromAmount('0');
			}
			return;
		}

		if (!fromTokenOraclePrice || !toTokenOraclePrice) {
			// we shouldn't update swap details if for some reason oracle prices are missing
			return;
		}

		const { swapInAmount, swapOutAmount } = getSimulatedQuoteValues(
			desiredSwapAmount,
			swapMode,
			fromTokenOraclePrice,
			toTokenOraclePrice
		);

		if (swapMode === 'ExactIn') {
			setSwapToAmount(swapOutAmount.toNum().toString());
		} else {
			setSwapFromAmount(swapInAmount.toNum().toString());
		}

		setSwapPrice(tokenPairPrice);
	}

	const handleSetSwapFromAmount = (amount: string, callRouteFetch = true) => {
		setCallFetchRoute(callRouteFetch);
		setSwapFromAmount(amount);
	};

	const handleSetSwapToAmount = (amount: string, callRouteFetch = true) => {
		setCallFetchRoute(callRouteFetch);
		setSwapToAmount(amount);
	};

	function getSwapConfig(isExactIn: boolean) {
		if (isExactIn) {
			return {
				exactMarket: swapFromMarket,
				otherMarket: swapToMarket,
				exactAmount: swapFromAmount,
				setExactAmount: handleSetSwapFromAmount,
				setOtherAmount: handleSetSwapToAmount,
			};
		} else {
			return {
				exactMarket: swapToMarket,
				otherMarket: swapFromMarket,
				exactAmount: swapToAmount,
				setExactAmount: handleSetSwapToAmount,
				setOtherAmount: handleSetSwapFromAmount,
			};
		}
	}

	// If max size changes, check if the from amount needs to reduced.
	const swapFromAmountBigNumRef = useSyncedRef(swapFromAmountBigNum);
	useEffect(() => {
		if (swapFromAmountBigNumRef.current.gt(maxSwapSize)) {
			handleSetSwapFromAmount(maxSwapSize.print());
		}
	}, [maxSwapSize.toNum()]);

	const resetForm = () => {
		setSwapFromAmount('');
		setSwapToAmount('');
		setQuoteDetails(null);
		setSwapPrice(0);
	};

	const enableVersionedTransaction = () => {
		setSavedSettings({
			...savedSettings,
			enableVersionedTx: true,
		});
		notify({
			type: 'success',
			message: 'Versioned transaction enabled',
			description: 'You can now use swaps.',
		});
	};

	type SwapResult = 'success' | 'fail' | 'deferred';

	const handleSwap = async (): Promise<SwapResult> => {
		if (isInsufficientBalance) {
			notify({
				type: 'error',
				description: 'You cannot swap more than your balance',
				message: 'Swap failed',
			});

			return 'fail';
		}

		if (!quoteDetails) {
			console.error('Route details not provided for swap');
			return 'fail';
		}

		// hardcode 2 seconds disable after submit
		setIsApprovingTxn(true);
		setTimeout(() => {
			setIsApprovingTxn(false);
		}, 3000);

		try {
			if (savedSettings.showTradeConfirmation) {
				// lock in swap details to show in the modal
				setStore((s) => {
					s.swap = {
						fromMarketIndex: swapFromMarket.marketIndex,
						toMarketIndex: swapToMarket.marketIndex,
						poolId: swapFromMarket.poolId,
						swapDetails: {
							userKey: targetUserKey ?? '',
							amount: desiredSwapAmount.val,
							fromMarketIndex: swapFromMarket.marketIndex,
							toMarketIndex: swapToMarket.marketIndex,
							swapMode,
							quote: quoteDetails,
							onlyDirectRoutes: directRouteSwaps,
							slippageBps: quoteDetails?.slippageBps,
						},
					};
				});
				show({ type: 'swap_confirmation', props: {} });

				return 'deferred';
			}

			const swapToastId = getSwapToastId(
				swapFromMarket.marketIndex,
				swapToMarket.marketIndex
			);
			const result = await swap(swapToastId, {
				userKey: targetUserKey ?? '',
				amount: desiredSwapAmount.val,
				fromMarketIndex: swapFromMarket.marketIndex,
				toMarketIndex: swapToMarket.marketIndex,
				swapMode,
				quote: quoteDetails,
				onlyDirectRoutes: directRouteSwaps,
				slippageBps: quoteDetails?.slippageBps,
			});

			if (result) {
				// Invalidate Titan badge queries if swap was successful and used Titan client
				if (swapClient?.getClientType() === 'titan') {
					// Wait 1 minute and 30 seconds before invalidating badge queries
					// This allows time for backend processing to complete
					setTimeout(() => {
						invalidateTitanBadgeQueries();
					}, 30000); // 90 seconds = 1.5 minutes
				}

				return 'success';
			}

			return 'fail';
		} catch (err) {
			console.error('Error swapping', err);
			return 'fail';
		} finally {
			getRoute();
		}
	};

	const calcOtherAmountAtMaxSlippage = () => {
		const otherAmount = +(
			(isExactIn ? quoteDetails?.outAmount : quoteDetails?.inAmount) ?? '0'
		);

		const slippagePreference =
			slippageTolerance === 'dynamic' ? 0.1 : slippageTolerance;

		const slippageBps = quoteDetails?.slippageBps ?? slippagePreference;

		const slippageToApply = isExactIn
			? 1 - slippageBps / 1000
			: 1 + slippageBps / 1000;

		const otherAmountAtMaxSlippage = (
			(otherAmount *
				(isNaN(slippageToApply) ? slippagePreference : slippageToApply)) /
			(getSwapConfig(isExactIn).otherMarket.precision?.toNumber() ?? 1)
		).toLocaleString(Env.locale, {
			maximumFractionDigits:
				getSwapConfig(isExactIn).otherMarket.precisionExp?.toNumber() ?? 0,
			minimumFractionDigits:
				getSwapConfig(isExactIn).otherMarket.precisionExp?.toNumber() ?? 0,
		});

		return Number(otherAmountAtMaxSlippage) > 0
			? otherAmountAtMaxSlippage
			: '-';
	};

	return {
		enableVersionedTx,
		enableVersionedTransaction,
		swapMode,
		setSwapMode,
		swapFromAmount,
		setSwapFromAmount: handleSetSwapFromAmount,
		swapToAmount,
		setSwapToAmount: handleSetSwapToAmount,
		inputNumericAmount,
		getSwapConfig,
		quoteDetails,
		swapPrice,
		resetForm,
		handleSwap,
		isLoading,
		setIsLoading,
		isButtonDisabled,
		fromTokenBalance,
		maxLeverageAllowed,
		maxSwapSize,
		currentLeverage,
		isInsufficientBalance,
		calcOtherAmountAtMaxSlippage,
		isGeoblocked,
		borrowAmount,
		borrowToBeRepaid,
		isApprovingTxn,
		hasMaxSpotBalance: willExceedMaxSpotBalance,
		showPriceEstimateOracleDivergenceWarning,
		priceDivergencePct,
		isAboveMaxSwapSizeWithoutLeverage,
		isAccountMarginEnabled,
		isMarginCheckboxChecked,
		toggleIsMarginCheckboxChecked,
	};
}
